/** @jsxImportSource @emotion/react */
import { Spinner } from "@chakra-ui/spinner";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  IntegrationAPI,
  IWordpressCategory,
} from "../../../api/IntegrationAPI";
import { ProductsAPI } from "../../../api/Products";
import { TagApi } from "../../../api/Tags";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  IProduct,
  IWordpressIntegration,
  ProductType,
} from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import { IOption } from "../../../types/CommonTypes";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { AllIntegrationsAtom } from "./Integrations";
import { ProductItem } from "./ProductItem";
const Container = styled.div`
  width: 1000px;
  height: fit-content;

  border: 1px solid #eaeaeabf;

  background-color: #ffffff;

  margin: 40px 0px;

  padding: 24px;

  cursor: default;
`;

const InfoColumn = styled.div`
  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 16px;

  display: flex;
  align-items: stretch;
  justify-content: space-between;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const TextColumn = styled.div`
  max-width: 600px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 18px;
  color: #222222;
  font-weight: 700;
  margin-bottom: 4px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  color: #707070;
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: start;

  margin: 32px 0;

  grid-gap: 24px;
`;

const StyledAutocompleteSelectWrapper = styled.div`
  width: 420px;
`;

const ProductListContent = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 16px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;

  width: 100%;

  grid-gap: 32px;
`;

const ProductListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  width: 100%;

  grid-gap: 16px;
`;

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;

  padding-top: 16px;

  margin-top: 16px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

interface Props {
  handleSelected: (vle: SettingsTab) => void;
}

export default function WordpressCategories(props: Props): JSX.Element | null {
  const { handleSelected } = props;
  const user = useRecoilValue(UserAtom);
  const [selectedWPCategory, setSelectedWPCategory] = useState<IOption>({
    name: "",
    value: "",
  });
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const modal = useModal();

  const [tagsPool, setTagsPool] = useRecoilState(TagsAtom);

  const allIntegrations = useRecoilValue(AllIntegrationsAtom);

  const wordpressIntegration = useMemo(
    () => allIntegrations.find((i) => i.type === "WORDPRESS"),
    [allIntegrations]
  ) as IWordpressIntegration;

  const [wordpressCategories, setWordpressCategories] = useState<
    IWordpressCategory[]
  >([]);

  const [currentWPCategorySelected, setCurrentWPCategorySelected] =
    useState<IWordpressCategory | null>(null);

  const [allIntegrationCategories, setAllIntegrationCategories] = useState<
    IProduct[]
  >([]);

  useEffect(() => {
    if (!wordpressIntegration || wordpressIntegration.type !== "WORDPRESS")
      handleSelected(SettingsTab.INTEGRATIONS);

    getTags();

    (async () => {
      setIsLoading(true);

      let allIntegrationProducts = await getAllIntegrationProducts();
      setAllIntegrationCategories(allIntegrationProducts);

      let wordpressCategories = await IntegrationAPI.getWordpressCategories(
        wordpressIntegration
      );

      // Filter the already created ones
      let filteredWPCategories = wordpressCategories.filter(
        (tp) =>
          !allIntegrationProducts.find((ip) => ip.productId === `${tp.id}`)
      );

      setWordpressCategories(filteredWPCategories);
      setAutoCompleteSuggestions(
        filteredWPCategories.map((p) => ({
          name: p.name,
          value: `${p.id}`,
        }))
      );
      setIsLoading(false);
    })();
  }, [wordpressIntegration]);

  async function getAllIntegrationProducts() {
    const allIntegrationProducts = await ProductsAPI.get("WORDPRESS");
    return allIntegrationProducts;
  }

  async function getTags() {
    if (tagsPool) return;
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  function handleSelectedSearch(savedSearchOption: IOption) {
    const category = wordpressCategories.find(
      (p) => `${p.id}` === savedSearchOption.value
    );
    if (category) {
      setSelectedWPCategory({
        name: category.name,
        value: `${category.id}`,
      });
      setCurrentWPCategorySelected(category);
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!currentWPCategorySelected) return;

    await ProductsAPI.create({
      productId: `${currentWPCategorySelected.id}`,
      productName: currentWPCategorySelected.name,
      tagIds: [],
      type: ProductType.WORDPRESS,
      userUid: user.userUid,
    });

    setSelectedWPCategory({
      name: "",
      value: "",
    });

    setCurrentWPCategorySelected(null);

    let allIntegrationProducts = await getAllIntegrationProducts();

    // Filter the already created ones
    let filteredWPCategories = wordpressCategories.filter(
      (tp) => !allIntegrationProducts.find((ip) => ip.productId === `${tp.id}`)
    );

    setAllIntegrationCategories(allIntegrationProducts);
    setWordpressCategories(filteredWPCategories);
    setAutoCompleteSuggestions(
      filteredWPCategories.map((p) => ({
        name: p.name,
        value: p.name,
      }))
    );
  };

  return (
    <Container>
      <InfoColumn>
        <TextColumn>
          <Title>Wordpress Categories Integration</Title>
          <SubTitle>
            Please choose the categories you wish to add tags from the list
            below.
          </SubTitle>
        </TextColumn>

        <img src="./images/wordpress.png" alt="wordpress" />
      </InfoColumn>

      <FormWrapper>
        <StyledAutocompleteSelectWrapper>
          <StyledAutocompleteSelect
            options={autoCompleteSuggestions}
            onOptionSelect={handleSelectedSearch}
            selectedOption={selectedWPCategory}
            placeholder={
              isLoading ? "Getting categories..." : "Enter the category name"
            }
            disabled={isLoading}
          />
        </StyledAutocompleteSelectWrapper>
        <StyledButton disable={isLoading} onClick={handleSubmit}>
          Add
        </StyledButton>

        {isLoading && <Spinner />}
      </FormWrapper>

      <ProductListContent>
        <Title>Categories</Title>

        <ProductListWrapper>
          {allIntegrationCategories.map((product, index) => (
            <ProductItem
              // I only need the first one to load the tags
              type={product.type}
              cancelTagLoading={index !== 0}
              key={product._id}
              product={product}
              getAllIntegrationProducts={async () => {
                let allIntegrationProducts = await getAllIntegrationProducts();

                // Filter the already created ones
                let filteredCategories = wordpressCategories.filter(
                  (tp) =>
                    !allIntegrationProducts.find(
                      (ip) => ip.productId === `${tp.id}`
                    )
                );

                setAllIntegrationCategories(allIntegrationProducts);
                setWordpressCategories(filteredCategories);
                setAutoCompleteSuggestions(
                  filteredCategories.map((p) => ({
                    name: p.name,
                    value: `${p.id}`,
                  }))
                );
              }}
            />
          ))}
        </ProductListWrapper>
      </ProductListContent>

      <FooterColumn>
        <StyledButton onClick={() => handleSelected(SettingsTab.WORDPRESS)}>
          Back
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
